import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'

import ScreenLayout from '@/components/layout/ScreenLayout'
import EmptyState from '@/components/ui/EmptyState'
import Skeleton from '@/components/ui/Skeleton'
import TabBar, { TabPanel, type TabBarOption } from '@/components/ui/TabBar'
import TitleRow from '@/components/ui/TitleRow'
import RefreshRegion from '@/components/ui/RefreshRegion'
import UpdatingNote from '@/components/ui/UpdatingNote'
import { useCycleMap } from '@/features/signals/api/queries'
import { useOnline } from '@/hooks/useOnline'
import { lastUpdatedMeta, resolveViewState } from '@/lib/view-state'
import { loadCycleMap, cycleMapSearchSchema } from '@/screens/signals/CycleMapScreen.loader'
import CycleGrid from '@/screens/signals/components/CycleGrid'
import PastSessionEmpty from '@/screens/signals/components/PastSessionEmpty'
import SessionDatePicker from '@/screens/signals/components/SessionDatePicker'
import SignalsEmpty from '@/screens/signals/components/SignalsEmpty'
import SignalsLocked from '@/screens/signals/components/SignalsLocked'
import SignalsRouteError from '@/screens/signals/components/SignalsRouteError'
import { CycleGridSkeleton, PickerSkeleton } from '@/screens/signals/components/SignalsSkeletons'
import { TabsSkeleton, ToolbarSkeleton } from '@/screens/signals/components/SignalsTableSkeletons'
import SignalsToolbar from '@/screens/signals/components/SignalsToolbar'
import { signalsRailProps } from '@/screens/signals/components/signals-rail'
import { useIsPastSession } from '@/screens/signals/lib/session-status'
import { isSignalsLocked } from '@/screens/signals/lib/signals-ui'

const TITLE = 'Market Cycle Map'

/** The strip names the page, so the header is the bare title (`sr-only` from `lg`). */
const HEADER = <TitleRow title={TITLE} />

/** Ties the grouping tabs to the one panel under them. */
const TABS_ID = 'cycle-map'

const GROUPINGS: readonly TabBarOption[] = [
  { value: 'month', label: 'By Month' },
  { value: 'quarter', label: 'By Quarter' },
]

/**
 * The map while it loads: the grid on its own boxes, the read-line (20px, 12 under the grid) and
 * the legend (24 under that). No card: the grid sits on the page.
 */
function CycleCardSkeleton() {
  return (
    <div className="flex flex-col">
      <CycleGridSkeleton />
      <div className="mt-3 flex h-5 items-center">
        <Skeleton className="h-3 w-64" />
      </div>
      <div className="mt-6 flex h-4 items-center">
        <Skeleton className="h-3 w-full max-w-xs" />
      </div>
    </div>
  )
}

/** The tab row with the static date's shape at its right, 16px above the card. */
function CycleToolbarSkeleton() {
  return (
    <div aria-hidden="true" className="mb-6">
      <ToolbarSkeleton start={<TabsSkeleton className="w-48" />} end={<PickerSkeleton />} />
    </div>
  )
}

/*
 * The loaded page, block for block: the tab row (the static date at its
 * right) and the map (the 13 × 12 grid on its own boxes, the read-line, the
 * legend). No intro or footnote (owner 2026-10-12).
 */
function CycleMapPending() {
  return (
    <ScreenLayout
      pending
      pendingLabel="Loading the cycle map…"
      header={HEADER}
      {...signalsRailProps(TITLE)}
    >
      <div>
        <CycleToolbarSkeleton />
        <CycleCardSkeleton />
      </div>
    </ScreenLayout>
  )
}

function CycleMapError(props: ErrorComponentProps) {
  return (
    <SignalsRouteError
      {...props}
      header={HEADER}
      pageTitle={TITLE}
      title="The cycle map did not load"
    />
  )
}

export const Route = createFileRoute('/_app/market/signals/cycle-map')({
  validateSearch: cycleMapSearchSchema,
  loader: loadCycleMap,
  pendingComponent: CycleMapPending,
  errorComponent: CycleMapError,
  component: CycleMapScreen,
})

/**
 * Signal Zone · Market cycle map — each sector's average return by Nepali
 * month (or fiscal quarter) over the last few BS years: the season a sector
 * has tended to have, as the mock's continuous ramp on the app's up and down colours (S4).
 *
 * MAIN read keyed on `?by=` (and `?years=`, URL only), so `useQuery` +
 * `keepPreviousData`: switching to quarters keeps the month grid on screen,
 * under RefreshRegion's refresh line, until the quarter grid lands in its
 * place with no entrance replayed. The date at the toolbar's right is the session picker; the
 * map takes no date, so a past pick shows `PastSessionEmpty` (`asks/signals-dates.md`).
 * `data: null` is the empty state; a 403 is the locked state.
 */
function CycleMapScreen() {
  const { by, years, date } = Route.useSearch()
  const navigate = Route.useNavigate()
  const online = useOnline()
  const query = useCycleMap({ by, years })
  const view = resolveViewState(query, { online })
  const map = query.data
  const isUpdating = query.isPlaceholderData
  // The grouping on screen is the map's own while a switch loads, so the words never
  // describe a grid that is not the one beside them.
  const shown = map?.by ?? by
  const past = useIsPastSession(date)

  const toolbar = (
    <SignalsToolbar
      start={
        <TabBar
          options={GROUPINGS}
          value={by}
          ariaLabel="Group the map"
          idBase={TABS_ID}
          onChange={(next) => {
            void navigate({
              search: (prev) => ({ ...prev, by: next === 'quarter' ? 'quarter' : 'month' }),
            })
          }}
        />
      }
      end={
        <>
          <UpdatingNote when={isUpdating} />
          <SessionDatePicker
            value={date ?? null}
            onChange={(next) => {
              void navigate({ search: (prev) => ({ ...prev, date: next }) })
            }}
          />
        </>
      }
    />
  )

  return (
    <ScreenLayout header={HEADER} {...signalsRailProps(TITLE)}>
      <div>
        {toolbar}
        <TabPanel idBase={TABS_ID} value={by}>
          {past && date !== undefined ? (
            <PastSessionEmpty date={date} />
          ) : view === 'failed' || view === 'offline' ? (
            isSignalsLocked(query.error) ? (
              <SignalsLocked error={query.error} />
            ) : (
              <EmptyState
                kind={view}
                size="page"
                onRetry={() => void query.refetch()}
                retrying={query.isFetching}
                meta={view === 'failed' ? lastUpdatedMeta(query.dataUpdatedAt) : undefined}
                {...(view === 'failed' ? { title: 'Couldn’t load the cycle map' } : {})}
              />
            )
          ) : map === undefined ? (
            // Only before any answer at all: the loader has one for the entry URL.
            <CycleCardSkeleton />
          ) : map === null || map.rows.length === 0 || map.periods.length === 0 ? (
            <RefreshRegion updating={isUpdating}>
              <SignalsEmpty
                reason="list"
                title="No cycle map yet"
                description="There is not enough price history to average yet. Check back later."
              />
            </RefreshRegion>
          ) : (
            <RefreshRegion updating={isUpdating}>
              <CycleGrid map={map} grouping={shown} />
            </RefreshRegion>
          )}
        </TabPanel>
      </div>
    </ScreenLayout>
  )
}
